import { useState, useEffect } from 'react';
import { WORKERS, MATURITY_LEVELS } from '../workerConfig';

export type WorkerStatus = 'checking' | 'live' | 'degraded' | 'offline';

export interface WorkerStatuses {
  edgeApi: WorkerStatus;
  kvDemo: WorkerStatus;
  d1Demo: WorkerStatus;
  r2Demo: WorkerStatus;
  aiDemo: WorkerStatus;
  securityDemo: WorkerStatus;
  l1: WorkerStatus;
  l2: WorkerStatus;
  l3: WorkerStatus;
  l4: WorkerStatus;
  l5: WorkerStatus;
}

async function ping(url: string, healthPath = '/health'): Promise<WorkerStatus> {
  try {
    const res = await fetch(`${url}${healthPath}`, { signal: AbortSignal.timeout(5000) });
    if (res.ok) return 'live';
    return res.status === 503 ? 'degraded' : 'offline';
  } catch {
    return 'offline';
  }
}

export function useWorkerStatus(): WorkerStatuses {
  const [statuses, setStatuses] = useState<WorkerStatuses>({
    edgeApi: 'checking',
    kvDemo: 'checking',
    d1Demo: 'checking',
    r2Demo: 'checking',
    aiDemo: 'checking',
    securityDemo: 'checking',
    l1: 'checking',
    l2: 'checking',
    l3: 'checking',
    l4: 'checking',
    l5: 'checking',
  });

  useEffect(() => {
    const checks: Array<[keyof WorkerStatuses, string, string?]> = [
      ['edgeApi', WORKERS.edgeApi],
      ['kvDemo', WORKERS.kvDemo],
      ['d1Demo', WORKERS.d1Demo],
      ['r2Demo', WORKERS.r2Demo],
      ['aiDemo', WORKERS.aiDemo],
      ['securityDemo', WORKERS.securityDemo],
      ['l1', MATURITY_LEVELS.l1, '/'],
      ['l2', MATURITY_LEVELS.l2, '/'],
      ['l3', MATURITY_LEVELS.l3, '/'],
      ['l4', MATURITY_LEVELS.l4, '/'],
      ['l5', MATURITY_LEVELS.l5, '/'],
    ];

    let active = true;
    Promise.all(checks.map(async ([key, url, path]) => [key, await ping(url, path)] as const))
      .then((results) => {
        if (active) setStatuses(Object.fromEntries(results) as unknown as WorkerStatuses);
      });

    return () => {
      active = false;
    };
  }, []);

  return statuses;
}
